// Gift catalog grid. Mirrors data-component="gift-catalog" from the
// approved HTML. Four category tabs (shirts / caps / half / full)
// filter the catalog; a selected tile shows the picked checkmark.
//
// TODO(runtime): hydrate catalog from a GIFT_CATALOG read endpoint
// (proposed - TODO runtime). The underlying catalog list lives in
// dist/account-workflows.GIFT_CATALOG (confirmed in
// dist/account-workflows.mjs) but there is no HTTP read endpoint yet.
// Each tile's eligibility respects dist/account-workflows.giftEligibility
// (confirmed) and the per-item stock field.
import type { ReactNode } from 'react';
import {
  CATEGORY_LABELS,
  GIFT_CATALOG_BY_CATEGORY,
  type GiftCategoryKey,
  type GiftVariant,
} from '../mockData.js';

const CATEGORIES: Array<{ key: GiftCategoryKey; label: string }> = [
  { key: 'shirts', label: 'T-shirts' },
  { key: 'caps', label: 'Ball caps' },
  { key: 'half', label: '½ gallons' },
  { key: 'full', label: 'Full gallons' },
];

export interface GiftCatalogProps {
  category: GiftCategoryKey;
  selectedId: string;
  onCategoryChange: (key: GiftCategoryKey) => void;
  onSelect: (variant: GiftVariant) => void;
}

function GiftArt({ variant }: { variant: GiftVariant }): ReactNode {
  if (variant.kind === 'T-SHIRT') {
    return (
      <span className={`gift-art ${variant.tone}`}>
        <svg viewBox="0 0 48 48">
          <path d="m16 8-11 7 6 10 6-3v19h14V22l6 3 6-10-11-7c-2 6-14 6-16 0Z" />
        </svg>
      </span>
    );
  }
  if (variant.kind === 'BALL CAP') {
    return (
      <span className={`gift-art ${variant.tone}`}>
        <svg viewBox="0 0 48 48">
          <path d="M8 28v-5a16 16 0 0 1 32 0v5H8Zm0 0L3 34h25l12-6M25 8v20" />
        </svg>
      </span>
    );
  }
  return (
    <span className={`gift-art ${variant.tone}`}>
      <span className="product-monogram">{variant.monogram}</span>
    </span>
  );
}

export function GiftCatalog({
  category,
  selectedId,
  onCategoryChange,
  onSelect,
}: GiftCatalogProps): ReactNode {
  const items = GIFT_CATALOG_BY_CATEGORY[category];
  const label = CATEGORY_LABELS[category];
  return (
    <section className="card" data-component="gift-catalog">
      <div className="card-head">
        <div className="row">
          <span className="head-icon">
            <svg>
              <use href="#box" />
            </svg>
          </span>
          <h3>Choose a gift</h3>
        </div>
        <span className="active-pill">Sample eligible account</span>
      </div>
      <div
        className="gift-category-tabs"
        role="group"
        aria-label="Gift category"
      >
        {CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            type="button"
            aria-pressed={category === cat.key}
            onClick={() => onCategoryChange(cat.key)}
          >
            {cat.label}
          </button>
        ))}
      </div>
      <div className="gift-category-heading">
        <div>
          <h3>{label.title}</h3>
          <p>{label.description}</p>
        </div>
        <span>Sample catalog</span>
      </div>
      <div className="gift-catalog">
        {items.map((item) => {
          const isSelected = item.id === selectedId;
          const tileClass = [
            'gift-tile',
            isSelected ? 'picked' : '',
            item.available ? '' : 'unavailable',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <button
              key={item.id}
              type="button"
              className={tileClass}
              aria-pressed={isSelected}
              disabled={!item.available}
              onClick={() => onSelect(item)}
            >
              <GiftArt variant={item} />
              <span className="gift-kind">{item.kind}</span>
              <strong>{item.name}</strong>
              <small>{item.variant} &middot; Example</small>
              <span className={item.available ? 'stock-good' : 'stock-out'}>
                {item.available ? 'Available · Example' : 'Out of stock · Example'}
              </span>
            </button>
          );
        })}
      </div>
      <p className="gift-catalog-note">
        Design names, colors, product-size availability and stock are illustrative. Only
        gift-eligible catalog variants appear in the live selection.
      </p>
    </section>
  );
}
